"use client";
import dynamic from "next/dynamic";
import Navigation from "@/HomeFeatures/Navigation";
import { useState, useEffect, useRef } from "react";
import { SubscribeCTA } from "@/HomeFeatures/Cta";
import BlogGridView from "./common/BlogGridView";
import { formatWordPressPosts, formatWordPressCategory } from "@/lib/Utility";
import {
  getAllBlogsWithCategories,
  getAllCategoriesList,
  getThreeLatestBlogs,
} from "@/lib/ApiClient";
import { BlogPostList, BlogInterface } from "@/lib/Types";
import Loader from "@/components/Loader";
import Image from "next/image";
import Images from "../assets/assets";
import BlogHeroSlider from "./sections/BlogHeroSlider";

const ContactSection = dynamic(() => import("@/HomeFeatures/Contact2"));
type CategoryShape = { id: number; name: string };

export default function BlogListingPage(props: {
  initialBlogs?: BlogInterface[];
  initialCategories?: CategoryShape[];
  initialLatest?: BlogPostList;
  initialPage?: number;
  perPage?: number;
}) {
  const {
    initialBlogs = [],
    initialCategories = [],
    initialLatest = [],
    initialPage = 1,
    perPage = 6,
  } = props;
  const [blogs, setBlogs] = useState<BlogInterface[]>(initialBlogs);
  const [category, setCategory] = useState<CategoryShape[]>(initialCategories);
  const [activeCategory, setActiveCategory] = useState<number | null>(null);
  const [showLoader, setShowLoader] = useState<boolean>(
    initialBlogs.length === 0,
  );
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [page, setPage] = useState(initialPage);
  const [hasMore, setHasMore] = useState(true);
  const [latestBlogs, setLatestBlogs] = useState<BlogPostList>(initialLatest);
  const [loadingLatest, setLoadingLatest] = useState<boolean>(
    initialLatest.length === 0,
  );
  const controllerRef = useRef<AbortController | null>(null);

  const fetchLatestBlogs = async () => {
    try {
      setLoadingLatest(true);
      const data = await getThreeLatestBlogs();
      setLatestBlogs(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to fetch latest blogs:", err);
      setLatestBlogs([]);
    } finally {
      setLoadingLatest(false);
    }
  };

  const fetchAllCategories = async () => {
    try {
      const data = await getAllCategoriesList();
      const catArray = formatWordPressCategory(data);
      setCategory(catArray);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const fetchBlogs = async (
    pageNum: number,
    opts?: { isLoadMore?: boolean },
  ) => {
    const isLoadMore = Boolean(opts?.isLoadMore);

    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;

    try {
      if (isLoadMore) setLoadingMore(true);
      else setShowLoader(true);

      const categoryParam = activeCategory
        ? `&categories=${activeCategory}`
        : "";
      const data = await getAllBlogsWithCategories(
        categoryParam,
        pageNum,
        perPage,
      );

      if (!data || data.length === 0) {
        if (isLoadMore) setHasMore(false);
        else {
          setBlogs([]);
          setHasMore(false);
        }
        return;
      }

      const blogArray = formatWordPressPosts(data);
      if (isLoadMore) {
        setBlogs((prev) => [...prev, ...blogArray]);
      } else {
        setBlogs(blogArray);
      }

      setHasMore(data.length >= perPage);
    } catch (error: unknown) {
      if ((error as any)?.name === "AbortError") return;
      console.error("Error fetching blogs:", error);
      setHasMore(false);
    } finally {
      if (isLoadMore) setLoadingMore(false);
      else setShowLoader(false);
    }
  };

  useEffect(() => {
    if (initialCategories.length === 0) fetchAllCategories();
    if (initialLatest.length === 0) fetchLatestBlogs();
    if (initialBlogs.length === 0) fetchBlogs(1);
    return () => controllerRef.current?.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setPage(1);
    setHasMore(true);
    fetchBlogs(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCategory]);

  const handleLoadMore = async () => {
    if (!hasMore || loadingMore) return;
    const nextPage = page + 1;
    await fetchBlogs(nextPage, { isLoadMore: true });
    setPage(nextPage);
  };

  return (
    <>
      <section className="relative flex flex-col max-w-[100vw] overflow-hidden">
        <div className="relative w-full lg:min-h-[100vh]">
          {/* ✅ Background Image */}
          <Image
            src={Images?.blog.bg} // 🔥 optimized image
            alt="blog background"
            fill
            priority
            quality={70}
            className="object-cover"
            sizes="(max-width: 768px) 100vw, (max-width: 1536px) 50vw, 700px"
            fetchPriority="high"
          />

          {/* ✅ Overlay content */}
          <div className="relative z-10 flex flex-col pb-[50px]">
            <Navigation dark={true} />
            <div className="max-w-[5000px] pt-[120px] pb-[30px] mx-auto w-[calc(100%_-_10%)]">
              <h1 className="text-center text-white text-[length:var(--service-banner-title)] font-normal clash-font capitalize leading-[100%] max-w-[18ch] mx-auto">
                The Konstant Blog
              </h1>

              <p className="text-center text-white/50 text-[length:var(--banner-para)] max-w-[90%] mx-auto font-normal leading-[150%] tracking-tight py-4">
                Get the latest tech news and recent updates on AI, Cloud, IoT, web, and mobile application development, etc.
              </p>
            </div>
            <div className="py-[50px] hidden lg:block">
              {loadingLatest ? (
                <div className="flex justify-center items-center w-full py-10">
                  <Loader size={80} />
                </div>
              ) : latestBlogs?.length > 0 ? (
                <BlogHeroSlider slides={latestBlogs} />
              ) : (
                <div className="flex justify-center items-center w-full py-10 text-white/50">
                  <p>No Latest Blogs Available</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Blog Grid */}
      <section className="relative z-2 w-full">
        <div className="max-w-[2500px] mx-auto w-[calc(100%_-_10%)] py-[20px] xl:py-[150px]">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-[15px] mb-8">
            <div>
              <p className="text-[#636363] text-base font-medium uppercase tracking-tight pb-4">
                Our Blogs
              </p>
              <h2 className="text-[length:var(--section-title)] font-medium clash-font leading-[1.1]">
                <span className="text-[#00040b]">All Latest</span>
                <span className="hidden md:block" />
                <span className="text-[#225bd4]">Insights</span>
              </h2>
            </div>
            <div className="text-[#00040b]/70 text-[length:var(--hero-para)] leading-[150%] max-w-[60ch]">
              All of the latest blogs and insights from the team & web design
              world.
            </div>
          </div>

          <BlogGridView
            BlogPosts={blogs}
            categoryArray={category}
            isShowAward={true}
            activeCategory={activeCategory}
            setActiveCategory={setActiveCategory}
            showLoader={showLoader}
            handleLoadMore={handleLoadMore}
            loadingMore={loadingMore}
            hasMore={hasMore}
          />
        </div>
      </section>

      <section className="relative z-2 w-full">
        <SubscribeCTA />
      </section>

      <ContactSection />
    </>
  );
}
